import React, { memo, useCallback, useState } from 'react'
import { Typography, CircularProgress } from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { HideMinor, ViewMinor } from '@shopify/polaris-icons'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { Button } from 'src/components/shared/Button'

interface Props {
  title: string
  mask: string
  className?: any
  onFetchSensitiveData?: () => string
}

const SensitiveDataRowComponent = ({ title, mask, className, onFetchSensitiveData }: Props) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [fetchedValue, setFetchedValue] = useState<string | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const { hasPermission } = useCheckPermissions()

  const canViewSensitiveData = hasPermission(UserPermission.PersonallyIdentifiableInformationView)

  const handleToggleVisibility = useCallback(() => {
    if (!canViewSensitiveData) {
      return
    }

    if (!isVisible && onFetchSensitiveData && fetchedValue === null) {
      setIsLoading(true)
      setFetchError(null)

      try {
        const sensitiveData = onFetchSensitiveData()
        setFetchedValue(sensitiveData)
        setIsVisible(true)
      } catch (error) {
        setFetchError('Failed to load sensitive data')
        console.error('Error fetching sensitive data:', error)
      } finally {
        setIsLoading(false)
      }
    } else {
      setIsVisible((prev) => !prev)
    }
  }, [isVisible, onFetchSensitiveData, fetchedValue, canViewSensitiveData])

  const getDisplayValue = () => {
    if (fetchError) {
      return <span css={tw`text-red-500`}>{fetchError}</span>
    }

    if (canViewSensitiveData && isVisible && fetchedValue !== null) {
      return fetchedValue
    }

    return mask
  }

  if (!canViewSensitiveData && (!mask || mask.trim() === '')) {
    return null
  }

  return (
    <div
      css={[
        tw`py-[14px] border-0 border-b border-solid border-[#EDEDED] flex items-center justify-between`,
        className,
      ]}
    >
      <div css={tw`flex w-full flex-col gap-1.5`}>
        <Typography color={colors.SHADES_GREY_400} variant="h6">
          {title}
        </Typography>
        <div css={tw`flex items-center gap-2`}>
          <Typography
            style={{ wordWrap: 'break-word' }}
            css={tw`text-black flex-1`}
            variant="body2"
          >
            {getDisplayValue()}
          </Typography>
          {canViewSensitiveData && (
            <Button
              color="grey"
              size="small"
              disabled={isLoading}
              leftIcon={
                isLoading ? (
                  <CircularProgress size={20} />
                ) : isVisible ? (
                  <ViewMinor width={20} />
                ) : (
                  <HideMinor width={20} />
                )
              }
              className={tw`min-w-[36px]`}
              onClick={handleToggleVisibility}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export const SensitiveDataRow = memo(SensitiveDataRowComponent)
